import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { withLatestFrom, switchMap, catchError, of, map } from "rxjs";
import { QuoteClient } from "src/api-service";
import { selectSelectedCompany } from "../../../user/user.selectors";
import { selectQuotesState } from "./quotes.selectors";
import { QuotesActions } from "./quotes.actions";
import { normalizeError } from "../../../app-store-error.types";

// ============================================================================
// QUOTES LIST EFFECTS - Single effect, no chained actions
// ============================================================================

@Injectable()
export class QuotesEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly quoteClient = inject(QuoteClient);

    load$ = createEffect(() =>
        this.actions$.pipe(
            ofType(QuotesActions.load),
            withLatestFrom(
                this.store.select(selectSelectedCompany),
                this.store.select(selectQuotesState),
            ),
            switchMap(([action, selectedCompany, state]) => {
                const companyId = selectedCompany?.id;
                if (!companyId) {
                    return of(QuotesActions.loadFailure({
                        error: { message: "No company selected" },
                        toastKey: "QUOTE_LIST.TOAST.LOAD_ERROR",
                    }));
                }

                // Filters/sorting are already merged into state by the reducer on load
                const pageIndex = action.pagination?.pageIndex ?? state.pagination?.pageNumber ?? 1;
                const pageSize = action.pagination?.pageSize ?? state.pagination?.pageSize ?? 50;

                return this.quoteClient
                    .getFiltered(
                        companyId,
                        pageIndex,
                        pageSize,
                        state.searchFilter,
                        state.sortProperty,
                        state.sortAscending,
                        null,
                        state.selectedStatuses.length > 0 ? state.selectedStatuses : undefined,
                        state.selectedTypes.length > 0 ? state.selectedTypes : undefined,
                        state.customerCode ?? null,
                        state.salesPersonCode ?? null,
                        state.salesSupportPersonCode ?? null,
                        state.locationCode ?? null,
                        state.createdFrom ?? null,
                        state.createdTo ?? null,
                        state.validToFrom ?? null,
                        state.validToTo ?? null,
                    )
                    .pipe(
                        map(response => QuotesActions.loadSuccess({
                            quotes: response.data ?? [],
                            pagination: response.pagination,
                        })),
                        catchError(error => of(QuotesActions.loadFailure({
                            error: normalizeError(error),
                            toastKey: "QUOTE_LIST.TOAST.LOAD_ERROR",
                        }))),
                    );
            }),
        ),
    );
}
